import { Component, ChangeDetectionStrategy, ChangeDetectorRef, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { STRATEGY_COLUMNS, STRATEGY_ACTIONS, CHANNEL_ICONS } from '../data/constants/strategies.constants';
import { MessageService, ConfirmationService } from 'primeng/api';
import { ApiStrategiesService } from '../data/services/api/api-strategies.service';
import { StrategyService } from '../data/services/view/strategy.service';
import { Observable } from 'rxjs';
import { environment } from '@env/environment';

@Component({
  selector: 'app-strategies-list',
  templateUrl: './strategies-list.component.html',
  styleUrls: ['./strategies-list.component.scss'],
  providers: [MessageService, ConfirmationService],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class StrategiesListComponent implements OnInit {
  columns = STRATEGY_COLUMNS;
  actions = STRATEGY_ACTIONS;
  createEntity = false;
  selectedStrategyId: string | null = null;
  action = 'CREATE';
  channelIcons = CHANNEL_ICONS;
  
  // Error handling
  hasError = false;
  errorMessage = '';
  
  // Loading & Empty states
  isLoading = false;
  emptyMessage = 'No hay estrategias creadas. Crea tu primera estrategia para comenzar';
  
  additionalMainActions = [
    { label: 'Nueva Estrategia', icon: 'fa-solid fa-square-plus', action: 'CREATE' }
  ];

  constructor(
    public apiStrategiesService: ApiStrategiesService,
    public strategyService: StrategyService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    // El modal de edición ya no se usa desde query params
    // La edición se hace desde la vista de detalle
  }

  /**
   * Transforma una estrategia agregando campos computed para la tabla
   * ACTUALIZADO: Usa daysRange en lugar de daysOffset
   */
  private transformStrategy(strategy: any): any {
    return {
      ...strategy,
      // Campo computed: scenarioDisplay (Escenario + Rango de días)
      scenarioDisplay: this.strategyService.getScenarioDisplayText(
        strategy.scenario,
        strategy.daysRange
      ),
      // Campo computed: activeChannels (Lista de canales activos)
      activeChannels: this.getActiveChannelsList(strategy.channels)
    };
  }

  /**
   * Obtiene la lista de canales activos formateada
   */
  private getActiveChannelsList(channels: any): string {
    if (!channels) return '';
    
    const activeChannels = Object.keys(channels)
      .filter(key => channels[key]?.enabled)
      .map(key => {
        // Capitalizar primera letra
        return key.charAt(0).toUpperCase() + key.slice(1);
      });
    
    return activeChannels.join(', ') || 'Ninguno';
  }

  hideDialog() {
    this.createEntity = false;
    this.strategyService.currentStep = 1;
    this.strategyService.newEntity();
    this.action = 'CREATE';
    this.strategyService.currentStep = 1;
    this.cdr.markForCheck();
  }

  mainActions(flow: string) {
    switch (flow) {
      case 'CREATE':
        this.strategyService.newEntity();
        this.createEntity = true;
        this.action = 'CREATE';
        this.cdr.markForCheck();
        break;
    }
  }

  handleTableAction(event: any) {
    const { action, data } = event;
    
    switch (action) {
      case 'ACTIVATE':
        this.activateStrategy(data._id);
        break;
      case 'DEACTIVATE':
        this.deactivateStrategy(data._id);
        break;
      case 'DELETE':
        this.deleteStrategy(data._id);
        break;
    }
  }

  saveForm() {
    if (!this.strategyService.entity.valid) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Formulario Incompleto',
        detail: 'Por favor complete todos los campos requeridos y valide cada paso del wizard'
      });
      return;
    }

    // Usar el método del servicio para preparar datos
    const data = this.strategyService.getFormDataForSubmit();
    
    // Transformar scheduledTime vacíos a null (backend no acepta strings vacíos)
    Object.keys(data.channels).forEach(channel => {
      if (data.channels[channel].scheduledTime === '') {
        data.channels[channel].scheduledTime = null;
      }
    });
    
    if (this.action === 'CREATE') {
      this.apiStrategiesService.postCollection(data, 'create').subscribe((response: any) => {
        if (response.error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error al Crear',
            detail: response.msg
          });
          this.cdr.markForCheck();
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Estrategia Creada',
          detail: 'La estrategia se creó correctamente'
        });

        this.hideDialog();
        // Refrescar tabla (MainTableComponent lo maneja automáticamente)
      });
    } else {
      // Construir la ruta completa con el ID
      const path = `dripping/strategies/${this.selectedStrategyId}`;
      this.apiStrategiesService.patchCollection(data, environment.apiUrl, path).subscribe((response: any) => {
        if (response.error) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error al Actualizar',
            detail: response.msg
          });
          this.cdr.markForCheck();
          return;
        }

        this.messageService.add({
          severity: 'success',
          summary: 'Estrategia Actualizada',
          detail: 'La estrategia se actualizó correctamente'
        });

        this.hideDialog();
      });
    }
  }

  activateStrategy(id: string) {
    this.confirmationService.confirm({
      message: '¿Desea activar esta estrategia?',
      header: 'Confirmar Activación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Activar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.apiStrategiesService.activate(id).subscribe((response: any) => {
          if (response.error) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.msg
            });
            this.cdr.markForCheck();
            return;
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Estrategia Activada',
            detail: 'La estrategia se activó correctamente'
          });
          this.cdr.markForCheck();
        });
      }
    });
  }

  deactivateStrategy(id: string) {
    this.confirmationService.confirm({
      message: '¿Desea desactivar esta estrategia?',
      header: 'Confirmar Desactivación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Desactivar',
      rejectLabel: 'Cancelar',
      accept: () => {
        this.apiStrategiesService.deactivate(id).subscribe((response: any) => {
          if (response.error) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.msg
            });
            this.cdr.markForCheck();
            return;
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Estrategia Desactivada',
            detail: 'La estrategia se desactivó correctamente'
          });
          this.cdr.markForCheck();
        });
      }
    });
  }

  deleteStrategy(id: string) {
    this.confirmationService.confirm({
      message: '¿Está seguro de eliminar esta estrategia? Esta acción no se puede deshacer.',
      header: 'Confirmar Eliminación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Sí, Eliminar',
      rejectLabel: 'Cancelar',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        this.apiStrategiesService.deleteCollection(id).subscribe((response: any) => {
          if (response.error) {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: response.msg
            });
            this.cdr.markForCheck();
            return;
          }

          this.messageService.add({
            severity: 'success',
            summary: 'Estrategia Eliminada',
            detail: 'La estrategia se eliminó correctamente'
          });
          this.cdr.markForCheck();
        });
      }
    });
  }

  /**
   * Maneja errores de forma centralizada
   */
  handleError(error: any): void {
    this.hasError = true;
    this.errorMessage = error.message || 'Ocurrió un error al cargar los datos';
    console.error('Error in StrategiesListComponent:', error);
    
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail: this.errorMessage,
      life: 5000
    });
    this.cdr.markForCheck();
  }

  /**
   * Reintenta la operación después de un error
   */
  retry(): void {
    this.hasError = false;
    this.errorMessage = '';
    window.location.reload();
  }
}
